import { request, generateApiUrl } from '@/utils/ajax';
import {
    SimplifiedOrder,
    Order,
    OrderHistory,
    FeeConfiguration,
    OrderStatus,
    OrderFilterData,
    OrderType,
    OrderGroup,
    OrderAttachmentInfo,
    OrderAttachmentType,
} from '@/types';
import { getOpenBuyerOfferAttachmentUrl, getDownloadBuyerOfferAttachmentUrl } from './buyer-offers';
import { getProjectCatalogProductFileUrl } from './project-catalog';
import { getApiUrl } from '@ygtechnologies/bygr-frontend-component-library';

export function approveOrder(orderId) {
    return request(`api/order/${orderId}/approve`, {
        method: 'POST',
    });
}

export function rejectOrder(orderId: number, reason: string) {
    return request(`api/order/${orderId}/reject`, {
        method: 'POST',
        json: true,
        body: {
            reason,
        },
    });
}

export function cancelOrder(orderId: number, openBuyerOption: boolean = false) {
    return request(`api/order/${orderId}/cancel?openBuyerOption=${openBuyerOption}`, {
        method: 'POST',
    });
}

export function reopenOrderOptions(orderId: number) {
    return request(`api/order/${orderId}/reopen`, {
        method: 'POST',
    });
}

export function reopenOrderGroupOptions(orderGroupId: number) {
    return request(`api/order/${orderGroupId}/reopen/group`, {
        method: 'POST',
    });
}

export function cancelOrderGroup(orderGroupId: number) {
    return request(`api/order-group/${orderGroupId}/cancel`, {
        method: 'POST',
    });
}

export function setOrderResponsible(orderId: number, userId: number) {
    return request(`api/order/${orderId}/responsible`, {
        method: 'PUT',
        json: true,
        body: {
            applicationUserId: userId,
        },
    });
}

export function setOrderResponsibles(orderIds: number[], profileId: number | null) {
    return request(`api/order/responsible`, {
        method: 'PUT',
        json: true,
        body: {
            orderIds,
            profileId: profileId,
        },
    });
}

export function setOrderExcludeFromRoomFormFlag(orderId: number, flag: boolean) {
    return request(`api/order/${orderId}/exclude-from-room-form`, {
        method: 'PATCH',
        json: true,
        body: flag,
    });
}

export function getOrder(orderId: number): Promise<Order> {
    return request(`api/order/${orderId}`).then((x) => x.json());
}

export interface OrderSearchOptions {
    projectId?: number;
    residentialUnitIds?: number[];
    status?: OrderStatus[];
    ownerProfileIds?: number[];
    approvableOnly?: boolean;
    orderTypes?: OrderType[];
    searchText?: string;
    categoryIds?: number[];
    tagIds?: number[];
    sourceOptionGroupIds?: number[];
    residentialUnitGroupIds?: number[];
    defaultBuyerOptions?: boolean;
    projectDeadlineIds?: number[];
    orderDateFrom?: Date | string | null;
    orderDateTo?: Date | string | null;
}

export function searchOrders(options: OrderSearchOptions): Promise<Order[]> {
    return request(`api/order`, { query: options }).then((x) => x.json());
}

export function searchOrderGroups(options: OrderSearchOptions): Promise<OrderGroup[]> {
    return request(`api/order-group`, { query: options }).then((x) => x.json());
}

export function getOrderGroup(orderGroupId: number): Promise<OrderGroup> {
    return request(`api/order-group/${orderGroupId}`).then((x) => x.json());
}


export function getSingleUnitOrderRoomFormExportUrl(
    residentialUnitId: number,
    includeSketch: boolean = false,
    documentVersionId: number | null = null
) {
    return generateApiUrl(`/api/residential-unit/${residentialUnitId}/export/room-form`, {
        includeSketch,
        documentVersionId,
    });
}

export function getOrderHistory(orderId: number): Promise<OrderHistory[]> {
    return request(`api/order/${orderId}/history`).then((x) => x.json());
}

export function getSimplifiedOrders(projectId: number): Promise<SimplifiedOrder[]> {
    return request(`api/order/simplified/${projectId}`).then((x) => x.json());
}

export function getOrderFees(orderId: number): Promise<FeeConfiguration[]> {
    return request(`api/order/${orderId}/fees`).then((x) => x.json());
}

export function saveOrderFees(orderId: number, fees: FeeConfiguration[]) {
    return request(`api/order/${orderId}/fees`, {
        method: 'PUT',
        json: true,
        body: fees,
    });
}

export function saveOrderLineNote(orderLineId: number, note: string) {
    return request(`api/orderline/${orderLineId}/note`, {
        method: 'PUT',
        json: true,
        body: note,
    });
}

export function deleteOrderLineNote(orderLineId: number) {
    return request(`api/orderline/${orderLineId}/note`, { method: 'DELETE' });
}

export function getRelatedOrders(orderId: number): Promise<Order[]> {
    return request(`api/order/${orderId}/related`).then((x) => x.json());
}

export function getOrderFilterData(projectId: number): Promise<OrderFilterData> {
    return request(`api/order/${projectId}/filter-data`).then((x) => x.json());
}

export function getOpenOrderAttachmentUrl(attachment: OrderAttachmentInfo) {
    switch (attachment.attachmentType) {
        case OrderAttachmentType.OfferGroup:
            return getOpenBuyerOfferAttachmentUrl(attachment.id);
        case OrderAttachmentType.Option:
            return getProjectCatalogProductFileUrl(attachment.id);
    }
}

export function getDownloadOrderAttachmentUrl(attachment: OrderAttachmentInfo) {
    switch (attachment.attachmentType) {
        case OrderAttachmentType.OfferGroup:
            return getDownloadBuyerOfferAttachmentUrl(attachment.id);
        case OrderAttachmentType.Option:
            return getProjectCatalogProductFileUrl(attachment.id, { download: true });
    }
}

export function getOrderGroupPdfUrl(orderGroupId: number) {
    return generateApiUrl(`/api/order-group/${orderGroupId}/pdf`);
}
